import { Injectable } from "@angular/core";
import { MessageModel } from "../models/message.model";
import { MessageOverview } from "../models/message-overview.model";
import { MessageInfo } from "../models/message-info.model";

@Injectable({
  providedIn: "root"
})
export class UtilityService {
  constructor() { }

  /**
   * Weekdays needs to have sunday as the first day
   */
  public static svLocale = {
    firstDayOfWeek: 1,
    dayNames: [
      "söndag",
      "måndag",
      "tisdag",
      "onsdag",
      "torsdag",
      "fredag",
      "lördag"
    ],
    dayNamesShort: ["sön", "mån", "tis", "ons", "tor", "fre", "lör"],
    dayNamesMin: ["S", "M", "Ti", "O", "To", "F", "L"],
    monthNames: [
      "januari",
      "februari",
      "mars",
      "april",
      "maj",
      "juni",
      "juli",
      "augusti",
      "september",
      "oktober",
      "november",
      "december"
    ],
    monthNamesShort: [
      "jan",
      "feb",
      "mar",
      "apr",
      "maj",
      "jun",
      "jul",
      "agu",
      "sep",
      "okt",
      "nov",
      "dec"
    ],
    today: "Nu",
    clear: "Rensa"
  };

  public static validDate(dateToCheck: Date) {
    if (
      dateToCheck &&
      !dateToCheck
        .toString()
        .includes("0001-01-01") &&
      !dateToCheck
        .toString()
        .includes("01-01-0001") &&
      !dateToCheck
        .toString()
        .includes("0001/01/01") &&
      !dateToCheck
        .toString()
        .includes("01/01/0001") &&
      !dateToCheck
        .toString()
        .toLowerCase()
        .includes("invalid date")
    ) {
      return true;
    } else {
      return false;
    }
  }

  public static equals(obj1: any, obj2: any): boolean {
    if (typeof obj1 !== typeof obj2) {
      return false;
    }
    if (
      (obj1 === undefined && obj2 !== undefined) ||
      (obj2 === undefined && obj1 !== undefined) ||
      (obj1 === null && obj2 !== null) ||
      (obj2 === null && obj1 !== null)
    ) {
      return false;
    }
    if (typeof obj1 === "object") {
      if (Array.isArray(obj1)) {
        if (!Array.isArray(obj2) || obj1.length !== obj2.length) {
          return false;
        }
        for (let i = 0; i < obj1.length; i++) {
          if (!this.equals(obj1[i], obj2[i])) {
            return false;
          }
        }
      } else {
        for (let prop in obj1) {
          if (obj1.hasOwnProperty(prop)) {
            if (!obj2.hasOwnProperty(prop)) {
              return false;
            }

            if (obj1[prop] instanceof Date && obj2[prop] instanceof Date) {
              const date1 = new Date(obj1[prop]);
              const date2 = new Date(obj2[prop]);
              const totalMinutes1 = Math.floor(date1.valueOf() / 1000 / 60);
              const totalMinutes2 = Math.floor(date2.valueOf() / 1000 / 60);
              if (totalMinutes1 !== totalMinutes2) {
                return false;
              }
            } else if (!this.equals(obj1[prop], obj2[prop])) {
              return false;
            }
          }
        }
        for (let prop in obj2) {
          if (obj2.hasOwnProperty(prop)) {
            if (!obj1.hasOwnProperty(prop)) {
              return false;
            }
          }
        }
      }
      return true;
    }
    return obj1 === obj2;
  }

  /**
   * @description Method to check two dates against each other.
   * Useful for comparing i.e. validity from and to.
   * @param {Date=} firstDate Initial date. This is the one that is supposed to be smaller.
   * @param {Date=} lastDate Last date. This is the one that is supposed to be larger.
   * @returns {*} Boolean which states if firstDate is before lastDate.
   * If one date is missing, the method returns true.
   */
  public static compareDates(firstDate: Date, lastDate: Date): boolean {
    if (
      firstDate &&
      lastDate &&
      this.validDate(firstDate) &&
      this.validDate(lastDate)
    ) {
      return firstDate.valueOf() < lastDate.valueOf();
    } else {
      return true;
    }
  }

  public static encodeTextContent(text: string | null): string | null {
    if (!text) {
      return text;
    }
    
    try {
      return btoa(unescape(encodeURIComponent(text)));
    } catch (error) {
      console.error('Failed to encode text content:', error);
      return text; // Fallback to original text if encoding fails
    }
  }
  /**
   * Decodes Base64 encoded text content.
   * Used for testing and debugging purposes.
   * 
   * @param encodedText Base64 encoded string
   * @returns Decoded original text, or null if input is null/empty
   */
  public static decodeTextContent(encodedText: string | null): string | null {
    if (!encodedText) {
      return encodedText;
    }
    
    try {
      // Decode from Base64
      return decodeURIComponent(escape(atob(encodedText)));
    } catch (error) {
      console.error('Failed to decode text content:', error);
      return encodedText; // Return as-is if decoding fails
    }
  }


  public static mapApiResponseToMessage(response: any): MessageModel {
    const message = new MessageModel();
    
    message.id = response.id || null;
    message.type = response.type?.toString() || null;
    message.ver = response.ver || 1;
    message.heading = response.heading || null;
    message.body = response.body || null;
    message.validFrom = response.validFrom ? new Date(response.validFrom) : null;
    message.validTo = response.validTo ? new Date(response.validTo) : null;
    message.createdAt = response.createdAt ? new Date(response.createdAt) : null;
    message.updatedAt = response.updatedAt ? new Date(response.updatedAt) : null;
    
    if (response.timeInterval) {
      message.timeIntervalEnabled = true;
      message.timeIntervalStart = response.timeInterval.timeIntervalStart || "00:00:00";
      message.timeIntervalEnd = response.timeInterval.timeIntervalEnd || "23:59:59";
    } else {
      message.timeIntervalEnabled = false;
      message.timeIntervalStart = "00:00:00";
      message.timeIntervalEnd = "23:59:59";
    }
    
    if (response.info) {
      message.info = new MessageInfo(
        response.info.action || null,
        response.info.affects || null,
        response.info.alternativeRoute || null,
        response.info.estimation || null,
        response.info.incident || null,
        response.info.location || null,
        response.info.notifyGroups || [],
        response.info.replacementBusConfirmed || false,
        response.info.replacementBusOrdered || false,
        response.info.ticketCooperation || false,
        response.info.trafficInfo || null,
        response.info.silentMtrp !== undefined ? response.info.silentMtrp : null,
        response.info.silentMalartag !== undefined ? response.info.silentMalartag : null
      );
    } else {
      message.info = new MessageInfo();
    }
    
    return message;
  }

  public static mapApiResponseArrayToMessages(responses: any[]): MessageModel[] {
    if (!responses || !Array.isArray(responses)) {
      return [];
    }
    return responses.map(response => this.mapApiResponseToMessage(response));
  }

  public static mapApiResponseToMessageOverview(response: any): MessageOverview {
    return new MessageOverview(
      response.id,
      response.messageStatus,
      new Date(response.from),
      response.to ? new Date(response.to) : null,
      response.heading,
      response.location || '',
      response.affected || '',
      response.createdOrUpdatedBy,
      new Date(response.lastUpdatedAt),
      response.messageType,
      response.timeInterval || null
    );
  }

  public static mapApiResponseArrayToMessageOverviews(responses: any[]): MessageOverview[] {
    if (!responses || !Array.isArray(responses)) {
      return [];
    }
    return responses.map(response => this.mapApiResponseToMessageOverview(response));
  }

  public static mapMessageToCreateRequest(message: MessageModel): any {
    let typeValue = 0;
    if (message.type) {
      const typeString = message.type.toString().toUpperCase();
      if (typeString === 'TI' || typeString === '0') {
        typeValue = 0;
      } else if (typeString === 'TOT' || typeString === '1') {
        typeValue = 1;
      } else {
        const parsed = parseInt(message.type);
        typeValue = isNaN(parsed) ? 0 : parsed;
      }
    }

    const shouldIncludeTimeInterval = message.timeIntervalEnabled && 
                                      message.timeIntervalStart && 
                                      message.timeIntervalEnd;

    const requestBody = {
      type: typeValue,
      heading: this.encodeTextContent(message.heading) || '',
      body: this.encodeTextContent(message.body) || '',
      validFrom: message.validFrom ? message.validFrom.toISOString() : new Date().toISOString(),
      validTo: message.validTo ? message.validTo.toISOString() : null,
      timeInterval: shouldIncludeTimeInterval ? {
        timeIntervalStart: message.timeIntervalStart,
        timeIntervalEnd: message.timeIntervalEnd
      } : null,
      info: message.info ? {
        affects: this.encodeTextContent(message.info.affects),
        location: this.encodeTextContent(message.info.location),
        incident: this.encodeTextContent(message.info.incident),
        estimation: this.encodeTextContent(message.info.estimation),
        action: this.encodeTextContent(message.info.action),
        trafficInfo: this.encodeTextContent(message.info.trafficInfo),
        alternativeRoute: this.encodeTextContent(message.info.alternativeRoute),
        ticketCooperation: message.info.ticketCooperation,
        replacementBusOrdered: message.info.replacementBusOrdered,
        replacementBusConfirmed: message.info.replacementBusConfirmed,
        notifyGroups: message.info.notifyGroups || [],
        silentMtrp: message.info.silentMtrp,
        silentMalartag: message.info.silentMalartag
      } : null
    };
    
    return requestBody;
  }

  public static mapMessageToVersionRequest(message: MessageModel): any {
    const baseRequest = this.mapMessageToCreateRequest(message);
    const { validTo, ...rest } = baseRequest;
    return {
      ...rest,
      validUntil: validTo
    };
  }

  public static removeElementFromArray(array, element) {
    const index = array.indexOf(element);

    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  public static createMessageCopy(message: MessageModel): MessageModel {
    let newMessage = JSON.parse(JSON.stringify(message));

    this.ensureMessageFromToDatesAreDateType(newMessage);

    return newMessage;
  }

  static ensureMessageFromToDatesAreDateType(message: MessageModel) {
    if (typeof (message.validFrom) == typeof ("string"))
      message.validFrom = new Date(Date.parse(message.validFrom!.toString()));
    if (typeof (message.validTo) == typeof ("string"))
      message.validTo = new Date(Date.parse(message.validTo!.toString()));
    if (typeof (message.createdAt) == typeof ("string"))
      message.createdAt = new Date(Date.parse(message.createdAt!.toString()));
    if (typeof (message.updatedAt) == typeof ("string"))
      message.updatedAt = new Date(Date.parse(message.updatedAt!.toString()));
  }
}